import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ResetPasswordSuccessGroup } from '../../components/Molecules';
import { ResetPasswordTemplate } from '../../components/Templates';
import { NOT_APPLICABLE_STRING } from '../../constants/general';
import { ResetPasswordResult } from '../../models/UserStore/UserStore';
import { useUserStore } from '../../stores/UserStore';
import { useShareData } from '../../context';

const { resetPassword } = useUserStore.getState();

interface State {
  email: string;
  password: string;
  retypePassword: string;
  isLoading: boolean;
  errorMessage: string;
  successResetPassword: boolean;
}

const ResetPasswordScreen: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const token = queryParams.get('token');

  const fallbackEmail = new URLSearchParams(
    new URL(window.location.href).search,
  )
    .get('email')
    ?.replace(/ /g, '+');

  const finalEmail = email?.includes(' ') ? fallbackEmail : email;

  const [state, setState] = useState<State>({
    email: finalEmail ?? NOT_APPLICABLE_STRING,
    password: '',
    retypePassword: '',
    isLoading: false,
    errorMessage: '',
    successResetPassword: false,
  });

  const onPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, password: event.target.value }));
    },
    [],
  );
  const onRetypePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, retypePassword: event.target.value }));
    },
    [],
  );

  const onClick = useCallback(async () => {
    if (state.password !== state.retypePassword) {
      setState((s) => ({ ...s, errorMessage: 'Passwords must match' }));
    } else if (state.password.length < 6) {
      setState((s) => ({
        ...s,
        errorMessage: 'Password(s) must have at least 6 characters',
      }));
    } else if (state.password === state.retypePassword && state.password) {
      setState((s) => ({ ...s, errorMessage: '' }));
      if (finalEmail && token) {
        setState((s) => ({ ...s, isLoading: true }));
        const res = await resetPassword({
          email: state.email,
          token,
          password: state.password,
        });
        setState((s) => ({ ...s, isLoading: false }));
        if (res === ResetPasswordResult.Success) {
          // navigate('/');
          setState((s) => ({ ...s, successResetPassword: true }));
        } else {
          toast.error(t('There is something wrong.'));
        }
      }
    }
  }, [finalEmail, state.email, state.password, state.retypePassword, t, token]);

  return state.successResetPassword === false ? (
    <ResetPasswordTemplate
      password={{ value: state.password, onChange: onPasswordChange }}
      retypePassword={{
        value: state.retypePassword,
        onChange: onRetypePasswordChange,
      }}
      onClick={() => {
        onClick().catch(console.error);
      }}
      isLoading={state.isLoading}
      errorMessage={state.errorMessage}
    />
  ) : (
    <ResetPasswordSuccessGroup email={state.email} />
  );
};

export default ResetPasswordScreen;
